import { useMutation } from "@apollo/client";
import { ProductFeature } from "@app/shared/types";
import { IconButton, Snackbar, SnackbarContent } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { analyticsTrack } from "app/analytics/track";
import { GRAPHQL_MUTATION_MARK_NEW_FEATURE_VIEWED } from "app/queries";
import { fetchUser, selectUser } from "features/auth/auth";
import { useBreakpointQuery } from "hooks/useBreakPointQuery";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

const NEW_FEATURE_MESSAGE = (
    <>Your Home Screen has changed. You can find your Mentorship group in the navigation menu.</>
);
const NEW_FEATURE_ID = "group-changes";
const NEW_FEATURE_DATE = DateTime.utc(2022, 4, 6);
const NEW_FEATURE_WINDOW_END = NEW_FEATURE_DATE.plus({ weeks: 2 });

const useStyles = makeStyles((theme) => ({
    snackbar: {
        maxWidth: 450,
    },
    content: {
        padding: theme.spacing(4, 3, 3, 3),
    },
    closeButton: {
        position: "absolute",
        top: theme.spacing(1),
        right: theme.spacing(1),
    },
}));

export const NewFeatureNotification = (props: { newFeatureUpdateSeen: string }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const user = useSelector(selectUser);

    const newFeatureOpenedDate = DateTime.fromISO(props.newFeatureUpdateSeen);
    const isMobile = useBreakpointQuery("sm");

    const [open, setOpen] = useState(
        // NOTE: we want to shop this current notification for ONLY immersion members
        // remove if desired outcome is to be visible to all members
        user?.features?.includes(ProductFeature.DEDICATED_SANGHA) &&
            newFeatureOpenedDate < NEW_FEATURE_DATE &&
            DateTime.now() < NEW_FEATURE_WINDOW_END,
    );

    useEffect(() => {
        if (open) {
            analyticsTrack(`member.newFeatureNotification.show`, {
                featureId: NEW_FEATURE_ID,
            });
        }
    }, [open]);

    const [markNewFeatureViewed] = useMutation(GRAPHQL_MUTATION_MARK_NEW_FEATURE_VIEWED);

    const handleClose = (eventName: string) => {
        analyticsTrack(`member.newFeatureNotification.${eventName}`, {
            featureId: NEW_FEATURE_ID,
        });
        setOpen(false);
        markNewFeatureViewed().then(() => {
            dispatch(fetchUser());
        });
    };

    const action = (
        <>
            <IconButton
                size="small"
                className={classes.closeButton}
                aria-label="close"
                onClick={() => handleClose("close")}
            >
                <CloseRoundedIcon fontSize="small" />
            </IconButton>
        </>
    );
    return (
        <Snackbar
            className={classes.snackbar}
            open={!isMobile && open}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
            <SnackbarContent
                className={classes.content}
                message={NEW_FEATURE_MESSAGE}
                action={action}
            />
        </Snackbar>
    );
};
